import type { AuthedRequester } from "@/lib/brokerAuth";
import type { AccountBond } from "@/lib/brokerAccounts";
import type { BondFieldValue } from "@/lib/bondFields";
import type { BondSubmission } from "@/lib/brokerBonds";
import {
  BOND_KINDS,
  bondKindLabel,
  readBondRows,
  type BondKind,
  type BondRowView,
} from "@/lib/bondStateModel";
import { bondTypeLabel } from "@/lib/bondTypeLabel";

// BRK-S412 — servicing an ISSUED bond: a rider request (PMS-S850) and a claim
// (PMS-S851), both addressed to the submission the bond came out of.
//
// Both routes were consumed ahead of the pin and are pinned since BRK-S511
// (`test/lib/accountActionsContract.test.ts`, BOND_SEAM_SURFACES). The
// account's Actions menu opens the two dialogs; the submission page's action
// row (BRK-S420 rider, BRK-S428 claim) mounts the same ones with its own bond
// preselected.

const SUBMISSIONS = "/api/broker/bond/submissions";

// ── The bonds a rider or claim can be against ───────────────────────────────

/** One issued bond the dialogs offer: where to post, and how it reads. */
export interface IssuedBondChoice {
  /** The source submission's pointer id — the route's `{id}`. */
  submissionId: string;
  bondId: string;
  /** "SB-xxxxx · {kind} · {obligee}". */
  label: string;
}

const isBondKind = (v: string): v is BondKind =>
  BOND_KINDS.some((k) => k === v);

/** The kind in words: the v2 vocabulary (supply and maintenance included),
 *  else the issued-bond label's own fallback — never blank. */
function kindLabel(kind: string): string {
  return isBondKind(kind) ? bondKindLabel(kind) : bondTypeLabel(kind);
}

const joined = (parts: readonly (string | null | undefined)[]) =>
  parts
    .filter((p): p is string => typeof p === "string" && p !== "")
    .join(" · ");

/**
 * The account's ISSUED bonds (`GET /accounts/{id}/bonds`, pinned) as choices.
 * Since PMS-S836 the read lists bonds in flight and ended too, each with its
 * served `status`; only an `issued` one takes a rider (D16) or a claim, so the
 * rest are not offered. A bond with no source submission is left out: both
 * routes address the submission, so there would be nowhere to post it.
 */
export function issuedBondChoicesFromAccountBonds(
  bonds: readonly AccountBond[],
): IssuedBondChoice[] {
  return bonds.flatMap((b) =>
    b.orderId === null || b.status !== "issued"
      ? []
      : [
          {
            submissionId: b.orderId,
            bondId: b.bondId,
            label: joined([
              b.orderNumber ?? b.bondNumber,
              kindLabel(b.bondType),
              b.obligeeName,
            ]),
          },
        ],
  );
}

/** A choice's select value: its submission and bond, both needed to post. */
export const issuedBondKey = (c: {
  submissionId: string;
  bondId: string;
}): string => `${c.submissionId}|${c.bondId}`;

/** The offered bonds — narrowed to one submission when the caller names it
 *  (the submission page) — and the one to start on. */
export function issuedBondOptions(
  bonds: readonly IssuedBondChoice[],
  submissionId?: string,
  bondId?: string,
): { options: IssuedBondChoice[]; initial: string } {
  const options =
    submissionId === undefined
      ? [...bonds]
      : bonds.filter((b) => b.submissionId === submissionId);
  const picked =
    options.find((b) => b.bondId === bondId) ??
    (options.length === 1 ? options[0] : undefined);
  return { options, initial: picked ? issuedBondKey(picked) : "" };
}

/** The chosen key back into its submission and bond, or null. */
export function chosenBond(
  options: readonly IssuedBondChoice[],
  key: string,
): IssuedBondChoice | null {
  return options.find((b) => issuedBondKey(b) === key) ?? null;
}

/** One submission's `bonds[]` (PMS-S836) as choices — the rows
 *  whose status is `issued`, for the submission page's own action row. */
export function issuedBondChoicesFromRows({
  submissionId,
  submissionNumber,
  obligeeName,
  rows,
}: {
  submissionId: string;
  submissionNumber: string;
  obligeeName: string | null;
  rows: readonly BondRowView[];
}): IssuedBondChoice[] {
  return rows
    .filter((r) => r.status === "issued")
    .map((r) => ({
      submissionId,
      bondId: r.id,
      label: joined([submissionNumber, bondKindLabel(r.kind), obligeeName]),
    }));
}

/** The submission page's offer (BRK-S428): the order's own `bonds[]`, read
 *  tolerantly, narrowed to the issued ones, under the obligee its header
 *  names. */
export function issuedBondChoicesOfOrder(
  order: { id: string; number: string; bonds?: unknown },
  obligeeName: string | null,
): IssuedBondChoice[] {
  return issuedBondChoicesFromRows({
    submissionId: order.id,
    submissionNumber: order.number,
    obligeeName,
    rows: readBondRows(order.bonds),
  });
}

// ── Riders (PMS-S850) ───────────────────────────────────────────────────────

/** What a rider changes (PMS-S850, P15) — `type`. */
export const RIDER_TYPES = [
  "increase_amount",
  "decrease_amount",
  "extend_dates",
  "change_name",
  "other",
] as const;
export type RiderType = (typeof RIDER_TYPES)[number];

export const RIDER_TYPE_LABELS: Record<RiderType, string> = {
  increase_amount: "Increase bond amount",
  decrease_amount: "Decrease bond amount",
  extend_dates: "Extend dates",
  change_name: "Change principal or obligee name",
  other: "Other",
};

/** A type this build has not adopted reads "Rider" — never blank. */
export function riderTypeLabel(type: string): string {
  return isRiderType(type) ? RIDER_TYPE_LABELS[type] : "Rider";
}

export function isRiderType(v: unknown): v is RiderType {
  return RIDER_TYPES.some((t) => t === v);
}

export interface RiderRequestInput {
  bondId: string;
  type: RiderType;
  details: string;
  /** The bond details the rider changes: ONLY the changed final-step fields,
   *  in the contract's spelling (D-10 — ISO dates, plain numbers); `null`
   *  clears one. Sent from the submission page, where the fields are drawn
   *  (BRK-S420); the account menu sends the same since BRK-S495. */
  values?: Record<string, BondFieldValue | null>;
}

/**
 * POST /api/broker/bond/submissions/{id}/riders (PMS-S850). The 201 is the
 * ORDER VIEW, not a receipt (BRK-S511): `changeRequest {kind: "rider",
 * status: "pending", id, fields}`, the bond's `pendingRider {id, type,
 * requestedAt}` and `actions.rider` off — so the page shows the server's
 * answer without a second read.
 */
export function requestBondRider(
  request: AuthedRequester,
  submissionId: string,
  input: RiderRequestInput,
): Promise<BondSubmission> {
  return request<BondSubmission>(
    `${SUBMISSIONS}/${encodeURIComponent(submissionId)}/riders`,
    { method: "POST", body: input },
  );
}

/**
 * POST /api/broker/bond/submissions/{id}/riders/{riderId}/withdraw
 * (PMS-S850, P15). Withdraws the pending rider the order's
 * `changeRequest.id` names; the 2xx is the order view, which the page shows,
 * so the banner clears from the server's answer and nothing else.
 */
export function withdrawBondRider(
  request: AuthedRequester,
  submissionId: string,
  riderId: string,
): Promise<BondSubmission> {
  return request<BondSubmission>(
    `${SUBMISSIONS}/${encodeURIComponent(submissionId)}/riders/${encodeURIComponent(riderId)}/withdraw`,
    { method: "POST" },
  );
}

/** When the pending rider was submitted: the served
 *  `bonds[].pendingRider.requestedAt` (PMS-S836), or null. */
export function pendingRiderRequestedAt(order: {
  bonds?: unknown;
}): string | null {
  return (
    readBondRows(order.bonds).find((b) => b.pendingRider !== null)?.pendingRider
      ?.requestedAt ?? null
  );
}

// ── Claims (PMS-S851) ───────────────────────────────────────────────────────

export interface ClaimInput {
  bondId: string;
  claimant: string;
  /** Dollars, a plain number on the wire (D-10 rule 3). */
  amount: number;
  description: string;
}

/** PMS's answer (PMS-S851): the claim's id, its CLM-##### number, the bond,
 *  `status: "received"` and PMS's own line for the broker (`message`). Read
 *  tolerantly all the same: the confirmation falls back to the number. */
export interface ClaimReceipt {
  id?: string;
  claimNumber?: string | null;
  bondId?: string;
  status?: string;
  message?: string | null;
}

/**
 * POST /api/broker/bond/submissions/{id}/claims (PMS-S851). Refused with
 * 409 `CLAIM_NOT_ALLOWED` whenever `actions.claim` is off (the matrix's own
 * sentence), and 422 `NO_ISSUED_BOND` naming `details.field: "bondId"`.
 */
export function reportBondClaim(
  request: AuthedRequester,
  submissionId: string,
  input: ClaimInput,
): Promise<ClaimReceipt> {
  return request<ClaimReceipt>(
    `${SUBMISSIONS}/${encodeURIComponent(submissionId)}/claims`,
    { method: "POST", body: input },
  );
}

/** The claim dialog's confirmation, said only from the 2xx: PMS's own line,
 *  else the claim number it gave, else that it was received. */
export function claimReceivedLine(
  receipt: ClaimReceipt | null | undefined,
): string {
  const message = receipt?.message;
  if (typeof message === "string" && message.trim() !== "") return message;
  const number = receipt?.claimNumber;
  return typeof number === "string" && number.trim() !== ""
    ? `Claim ${number} received`
    : "Claim received";
}
